import React, { useState } from 'react';
import {
  Typography,
  Stack,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  TablePagination,
} from '@mui/material';
import { useQuery } from 'react-query';

import { MainLayout } from '../components/layout/MainLayout';
import { api, callsAPI } from '../services/api';
import { useToast } from '../components/common/ToastProvider';

const formatDate = (dateString?: string) => {
  if (!dateString) return '-';
  const d = new Date(dateString);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

export const IncomingCallsPage: React.FC = () => {
  const { showToast } = useToast();

  const [page, setPage] = useState(0);
  const rowsPerPage = 20;
  const [audioErrorsShown, setAudioErrorsShown] = useState<Set<string>>(
    new Set()
  );

  const getIncomingCallsQuery = useQuery(
    ['callsAPI.getIncomingCalls', page],
    () => callsAPI.getIncomingCalls({ page }),
    {
      onError: (err) => {
        console.error(err);
        showToast('Failed to load incoming calls', 'error');
      },
    }
  );

  const handleAudioError = async (callId: string) => {
    if (audioErrorsShown.has(callId)) return;
    try {
      await api.head(`call/${callId}/recording/incoming`);
    } catch (err: any) {
      if (err.response?.status === 404) {
        showToast(
          'Recording file not found. It may have been deleted from the remote server.',
          'error'
        );
        setAudioErrorsShown((prev) => new Set(prev).add(callId));
      }
    }
  };

  const renderContent = () => {
    if (getIncomingCallsQuery.isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    const calls = getIncomingCallsQuery.data?.[0] ?? [];
    const total = getIncomingCallsQuery.data?.[1] ?? 0;

    if (calls.length === 0) {
      return (
        <Typography color='text.secondary'>
          No incoming calls available.
        </Typography>
      );
    }

    return (
      <>
        <TableContainer
          component={Paper}
          variant='outlined'
        >
          <Table size='small'>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.100' }}>
                <TableCell>
                  <Typography variant='subtitle2'>Recording</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='subtitle2'>Caller Number</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='subtitle2'>Called Number</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='subtitle2'>Created At</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='subtitle2'>Has Voicemail</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {calls.map((call) => (
                <TableRow
                  key={call.id}
                  hover
                >
                  <TableCell>
                    <audio
                      src={callsAPI.getIncomingRecordingUrl(call.id)}
                      controls
                      style={{ height: 30, width: 180 }}
                      preload='none'
                      onError={() => handleAudioError(call.id)}
                    />
                  </TableCell>
                  <TableCell>{call.callerNumber}</TableCell>
                  <TableCell>{call.calledNumber}</TableCell>
                  <TableCell>{formatDate(call.createdAt)}</TableCell>
                  <TableCell>
                    <Chip
                      label={call.hasVoicemail ? 'Yes' : 'No'}
                      size='small'
                      color={call.hasVoicemail ? 'success' : 'default'}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component='div'
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[]}
          showFirstButton
          showLastButton
        />
      </>
    );
  };

  return (
    <MainLayout>
      <Stack
        spacing={4}
        width='100%'
      >
        <Typography
          variant='h4'
          gutterBottom
        >
          Incoming Calls
        </Typography>
        {renderContent()}
      </Stack>
    </MainLayout>
  );
};
